import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Check, Loader2, Sparkles, Calendar } from "lucide-react";
import {
  getMySubscription,
  listActivePlans,
  createSubscriptionOrder,
  listMySubscriptionOrders,
} from "@/lib/api/subscriptions.functions";
import { launchPayfastCheckout } from "@/lib/payfast-launch";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/subscription")({
  component: SubscriptionPage,
});

function fmtZar(n: number) {
  return `R${Number(n).toFixed(2)}`;
}

function SubscriptionPage() {
  const getSub = useServerFn(getMySubscription);
  const getPlans = useServerFn(listActivePlans);
  const getOrders = useServerFn(listMySubscriptionOrders);
  const create = useServerFn(createSubscriptionOrder);
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);

  const sub = useQuery({ queryKey: ["my-sub"], queryFn: () => getSub() });
  const plans = useQuery({ queryKey: ["sub-plans"], queryFn: () => getPlans() });
  const orders = useQuery({
    queryKey: ["my-sub-orders"],
    queryFn: () => getOrders(),
  });

  const startCheckout = useMutation({
    mutationFn: (planCode: string) => create({ data: { planCode } }),
    onSuccess: async ({ reference }) => {
      toast.success("Redirecting to PayFast…");
      await launchPayfastCheckout(reference);
    },
    onError: (err: any) => {
      setBusy(null);
      toast.error(err?.message || "Could not start checkout");
    },
  });

  const subscription = sub.data?.subscription as any;
  const trial = sub.data?.trial;
  const subActive = !!sub.data?.subscriptionActive;

  return (
    <div className="space-y-8">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Membership</p>
        <h2 className="mt-1 font-display text-2xl text-navy">My subscription</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Every new member gets a 24-hour free trial. Continue with a paid plan to keep using member-only services.
        </p>
      </header>

      {/* Status card */}
      <section className="rounded-xl border border-border bg-card p-5 shadow-soft">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Subscription</p>
            <p className="mt-1 text-lg font-semibold text-navy">
              {subActive ? `${subscription?.plan_code} — Active` : "No active subscription"}
            </p>
            {subActive && subscription?.current_period_end && (
              <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" /> Renews{" "}
                {new Date(subscription.current_period_end).toLocaleDateString()}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Trial</p>
            <p className="mt-1 text-lg font-semibold text-navy">
              {trial?.active ? "Active" : "Expired"}
            </p>
            {trial?.endsAt && (
              <p className="mt-1 text-xs text-muted-foreground">
                {trial.active ? "Ends" : "Ended"} {new Date(trial.endsAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section>
        <h3 className="mb-3 font-display text-xl text-navy">Choose a plan</h3>
        {plans.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading plans…
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            {(plans.data?.plans ?? []).map((p: any) => {
              const isCurrent = subActive && subscription?.plan_code === p.code;
              const available = p.is_active && Number(p.monthly_price_zar) > 0;
              return (
                <div
                  key={p.code}
                  className={`flex flex-col rounded-xl border p-5 shadow-soft ${
                    isCurrent ? "border-gold bg-gold/5" : "border-border bg-card"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-gold" />
                    <h4 className="font-display text-lg text-navy">{p.name}</h4>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
                  <p className="mt-3 text-2xl font-bold text-navy">
                    {available ? fmtZar(p.monthly_price_zar) : "Coming soon"}
                    {available && (
                      <span className="text-sm font-normal text-muted-foreground"> /month</span>
                    )}
                  </p>
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {(p.features ?? []).map((f: string) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    disabled={
                      !available || isCurrent || busy === p.code || startCheckout.isPending
                    }
                    onClick={() => {
                      setBusy(p.code);
                      startCheckout.mutate(p.code);
                    }}
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground transition hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busy === p.code ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Redirecting…
                      </>
                    ) : isCurrent ? (
                      "Current plan"
                    ) : available ? (
                      `Subscribe — ${fmtZar(p.monthly_price_zar)}/mo`
                    ) : (
                      "Not available yet"
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Orders */}
      <section>
        <h3 className="mb-3 font-display text-xl text-navy">Subscription payments</h3>
        {orders.data?.orders?.length ? (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.data.orders.map((o: any) => (
                  <tr key={o.reference} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-xs">{o.reference}</td>
                    <td className="px-3 py-2">{o.plan_code}</td>
                    <td className="px-3 py-2 text-right">{fmtZar(o.amount_zar)}</td>
                    <td className="px-3 py-2 capitalize">{o.status}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(o.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No subscription payments yet.</p>
        )}
      </section>
    </div>
  );
}
